import { create } from "zustand";
import { useStoreHome } from "../Home/useStoreHome";
import { MensajeError, MensajeToastExito } from "../../componentes/FuncionesLocales";
import {
  copiarListaPrecio,
  guardarListaPrecio,
  listarListasPrecios,
  obtenerClientesDisponibles,
  obtenerClientesLista,
  obtenerMonedaTipos,
  obtenerMercaderiasDisponibles,
  obtenerMercaderiasLista,
} from "../../servicios/listasPreciosApi";

const fechaIso = (fecha) => {
  const valor = new Date(fecha);
  if (Number.isNaN(valor.getTime())) return "";
  return valor.toISOString().slice(0, 10);
};

const resumenNuevo = () => {
  const desde = new Date();
  const hasta = new Date();
  hasta.setFullYear(hasta.getFullYear() + 1);
  return {
    Id: 0,
    sDescripcion: "",
    bActivo: true,
    bListaPorDefecto: false,
    dFechaDesde: fechaIso(desde),
    dFechaHasta: fechaIso(hasta),
  };
};

const respuesta = (r) => {
  if (r?.error) throw new Error(r.error);
  return r?.data || {};
};

const obtenerLista = (datos, ...claves) => {
  for (const clave of claves) {
    if (Array.isArray(datos?.[clave])) return datos[clave];
  }
  return Array.isArray(datos) ? datos : [];
};

const combinarSeleccion = (asignados = [], disponibles = []) => {
  const mapa = new Map();
  disponibles.forEach((item) => mapa.set(String(item.Id), { ...item, bSeleccionado: false }));
  asignados.forEach((item) => mapa.set(String(item.Id), { ...item, bSeleccionado: true }));
  return [...mapa.values()].sort((a, b) =>
    String(a.sDescripcion || "").localeCompare(String(b.sDescripcion || ""), "es"),
  );
};

export const useStoreListasPrecios = create((set, get) => ({
  cargando: false,
  guardando: false,
  listas: [],
  resumen: resumenNuevo(),
  mercaderias: [],
  clientes: [],
  monedaTipos: [],
  editorActivo: false,

  inicializar: async () => {
    const base = useStoreHome.getState().EmpresaId;
    if (!base) return;
    set({ cargando: true });
    try {
      const monedas = respuesta(await obtenerMonedaTipos(base));
      set({ monedaTipos: obtenerLista(monedas, "Lista") });
      await get().cargarListas();
    } catch (error) {
      MensajeError(error.message);
    } finally {
      set({ cargando: false });
    }
  },

  cargarListas: async (seleccionarId) => {
    const base = useStoreHome.getState().EmpresaId;
    const data = respuesta(await listarListasPrecios(base));
    const listas = data.Lista || [];
    set({ listas });
    const id = seleccionarId || get().resumen.Id || listas[0]?.Id;
    if (id) await get().seleccionarLista(id);
    else set({
      editorActivo: false,
      resumen: resumenNuevo(),
      mercaderias: [],
      clientes: [],
    });
  },

  seleccionarLista: async (id) => {
    const lista = get().listas.find((item) => item.Id === Number(id));
    if (!lista) return;
    const base = useStoreHome.getState().EmpresaId;
    set({ cargando: true, editorActivo: true });
    try {
      const [productosAsignados, productosDisponibles, personas] = await Promise.all([
        obtenerMercaderiasLista(base, lista.Id),
        obtenerMercaderiasDisponibles(base),
        obtenerClientesLista(base, lista.Id),
      ]);
      const datosAsignados = respuesta(productosAsignados);
      const datosDisponibles = respuesta(productosDisponibles);
      const datosPersonas = respuesta(personas);
      set({
        resumen: {
          ...lista,
          dFechaDesde: fechaIso(lista.dFechaDesde),
          dFechaHasta: fechaIso(lista.dFechaHasta),
        },
        mercaderias: combinarSeleccion(
          obtenerLista(datosAsignados, "ListaAsignadas"),
          [
            ...obtenerLista(datosAsignados, "ListaDisponibles"),
            ...obtenerLista(datosDisponibles, "ListaDisponibles", "Lista"),
          ],
        ),
        clientes: combinarSeleccion(
          obtenerLista(datosPersonas, "ListaAsignados"),
          obtenerLista(datosPersonas, "ListaDisponibles"),
        ),
      });
    } catch (error) {
      MensajeError(error.message);
    } finally {
      set({ cargando: false });
    }
  },

  nuevaLista: async () => {
    const base = useStoreHome.getState().EmpresaId;
    set({ cargando: true, editorActivo: true });
    try {
      const [productos, personas] = await Promise.all([
        obtenerMercaderiasDisponibles(base),
        obtenerClientesDisponibles(base),
      ]);
      set({
        resumen: resumenNuevo(),
        mercaderias: combinarSeleccion(
          [],
          obtenerLista(respuesta(productos), "ListaDisponibles", "Lista"),
        ),
        clientes: combinarSeleccion(
          [],
          obtenerLista(respuesta(personas), "Lista", "ListaDisponibles"),
        ),
      });
    } catch (error) {
      MensajeError(error.message);
    } finally {
      set({ cargando: false });
    }
  },

  setResumen: (campo, valor) =>
    set({ resumen: { ...get().resumen, [campo]: valor } }),

  setMercaderia: (id, cambios) =>
    set({
      mercaderias: get().mercaderias.map((item) =>
        item.Id === id ? { ...item, ...cambios } : item,
      ),
    }),

  setCliente: (id, seleccionado) =>
    set({
      clientes: get().clientes.map((item) =>
        item.Id === id ? { ...item, bSeleccionado: seleccionado } : item,
      ),
    }),

  guardarLista: async () => {
    const { resumen, mercaderias, clientes } = get();
    if (!resumen.sDescripcion.trim()) {
      MensajeError("La descripción es obligatoria.");
      return false;
    }
    if (!resumen.dFechaDesde || !resumen.dFechaHasta || resumen.dFechaHasta < resumen.dFechaDesde) {
      MensajeError("Revisa el rango de vigencia.");
      return false;
    }
    set({ guardando: true });
    try {
      const data = respuesta(await guardarListaPrecio({
        EmpresaId: useStoreHome.getState().EmpresaId,
        resumen,
        mercaderias: mercaderias.filter((item) => item.bSeleccionado),
        clientes: clientes.filter((item) => item.bSeleccionado),
      }));
      await get().cargarListas(data.Dato);
      MensajeToastExito("Lista de precios guardada.");
      return true;
    } catch (error) {
      MensajeError(error.message);
      return false;
    } finally {
      set({ guardando: false });
    }
  },

  copiarLista: async ({ descripcion }) => {
    const { resumen } = get();
    if (!resumen.Id) return false;
    set({ guardando: true });
    try {
      const data = respuesta(await copiarListaPrecio({
        EmpresaId: useStoreHome.getState().EmpresaId,
        ListaId: resumen.Id,
        descripcion,
        fechaDesde: resumen.dFechaDesde,
        fechaHasta: resumen.dFechaHasta,
        asignarClientes: false,
      }));
      MensajeToastExito("Lista copiada correctamente.");
      await get().cargarListas(data.Dato);
      return true;
    } catch (error) {
      MensajeError(error.message);
      return false;
    } finally {
      set({ guardando: false });
    }
  },
}));
